import { z } from "zod";
import { ESTIMATE_MSG, Estimate, EstimateStatus, IssueRequest, LineInput, SheetInput } from "./estimate.js";
import { TaxChangedFromQuote, TaxDate } from "./tax.js";
import { InstallmentFigures, PlanBrief } from "./installment.js";
import { InvoicePayments, InvoiceState } from "./payment.js";
import { InvoiceClosing } from "./closing.js";
import { InstallmentChoice, InvoicePaySchedule } from "./paySchedule.js";
import { InvoiceRecurring } from "./recurring.js";
import { InvoiceNumbering } from "./numbering.js";
import { InvoiceImported } from "./imports.js";
import { SendSummary } from "./listFilter.js";

// ---------------------------------------------------------------------------
// 受注／失注 と 請求書（縦串5、024、docs/19）。
//   見積で受注が決まった工事 → 請求書を起こす（明細を写す）→ 直す → 出す（I-0001）→ PDF。
//   金額は見積と同じく DB（fn_recalc_all）だけが計算する。請求書の紙は都度請求の紙
//   （明細から税率ごとに1回丸め）。締め請求書（納品書を束ねる）は次の段
// ---------------------------------------------------------------------------

export const DECIDE_MSG = {
  notIssued: "発行した見積（未受注）だけ、受注・失注にできます",
  already: "この見積はもう受注か失注になっています",
  notDecided: "受注・失注にした見積だけ元に戻せます",
  hasInvoice: "請求書を作成したあとは元に戻せません。先に請求書の下書きを削除してください",
  decidedNoUnissue: "受注・失注にした見積は取り消せません。先に「元に戻す」で未受注に戻してください",
} as const;

export const Decision = z.enum(["ordered", "lost"]);
export type Decision = z.infer<typeof Decision>;
export const DecideRequest = z.object({ decision: Decision });
export type DecideRequest = z.infer<typeof DecideRequest>;
export const DecideResult = z.object({ projectId: z.string().uuid(), decidedAt: z.string() });
export type DecideResult = z.infer<typeof DecideResult>;

export const INVOICE_MSG = {
  /** 見出しの右の「⋯」（#172：主なボタン1つのほかの 取消・領収書・入金の行 など） */
  stateMenu: "発行した請求書のほかの動き",
  notFound: "その請求書は見つかりません",
  bankAccountNotFound: "その振込口座は見つかりません（削除されたか、別の会社のものです）",
  notDraft: "発行済みの請求書は修正できません。修正するときは取消で下書きに戻すか、訂正版を発行します",
  quoteNotOrdered: "受注した見積からだけ請求書を作成できます",
  draftExists: "この見積には請求書の下書きがあります。下書きを開きます",
  dueDateRequired: "お支払期限を入力してください",
  companyRequired: "会社名を入力してください。請求書の発行者として印字します",
  noLines: "明細がありません。行を追加してから発行してください",
  notIssued: "発行済みの請求書だけ取消できます（入金の記録があるものは取消できません）",
  unissueDisabled: "発行済みの請求書の取消は、設定で止めています。修正するときは訂正版を発行します",
  /** 下書きの紙を見る（#149：見積書と同じ文言） */
  pdfDraft: "PDF で確認（透かし入り）",
} as const;

export const InvoiceStatus = EstimateStatus;

/**
 * 請求書。見積（Estimate）と同じ形に、請求書だけの欄を足したもの。
 * 同じ形なので、見積のエディタ（行の編集・内訳・計算の見え方）をそのまま使える
 */
export const InvoiceBank = z.object({
  /** 口座の id（出したあと・口座なしは null） */
  id: z.string().uuid().nullable(),
  text: z.string(),
  source: z.enum(["issued", "selected", "default", "none"]),
});
export type InvoiceBank = z.infer<typeof InvoiceBank>;

export const Invoice = Estimate.extend({
  /** どの工事（受注した見積）の請求書か */
  project: z.object({
    /** 締め請求書で工事をまたぐと null（#267。束ねた{deliveryNote}の工事はそれぞれ「請求済み」になる） */
    id: z.string().uuid().nullable(),
    name: z.string(),
    /** 元になった見積 */
    quoteId: z.string().uuid().nullable(),
    quoteNo: z.string().nullable(),
    /** この工事で何枚目の請求書か（出したものを数える。追加分は2枚目） */
    invoiceCount: z.number().int(),
  }),
  revision: Estimate.shape.revision.extend({
    /** お支払期限（請求日から顧客の支払条件で計算。請求書ごとに直せる。出すときに必須） */
    dueDate: z.string().nullable(),
  }),
  /** 支払条件の日本語（「月末締め・翌月末払い」）。顧客に無ければ既定のもの */
  paymentTermsLabel: z.string(),
  /** その支払条件がどこで決まったか（039：案件の上書き → 顧客 → 既定）。古い形の読み込みのため省けてよい */
  paymentTermsSource: z.enum(["group", "client", "default"]).optional(),
  issued: Estimate.shape.issued.unwrap().extend({
    /** 登録番号があって適格請求書として出したか */
    isQualified: z.boolean(),
  }).nullable(),
  /** この請求書の領収書（#39、031。converts_from で結ばれたもの）。無ければ null */
  receipt: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: EstimateStatus }).nullable(),
  /** 分割請求の回（042、docs/30）。紙の上の「ご契約金額／これまでのご請求／今回のご請求／残り」。一括なら null */
  installment: InstallmentFigures.nullable().default(null),
  /** 分割の予定のあらまし（042）。出したあとの帯に「次：2回目（中間金）の請求書をつくる」を出す。一括なら null */
  plan: PlanBrief.nullable().optional(),
  /**
   * 紙に印字する振込先（043、docs/32）。source：issued＝出した時点で固めた値／selected＝この請求書で選んだ口座／
   * default＝会社の既定の口座／none＝口座なし（振込先なしで出せる）。text は「みずほ銀行（0001） 神保町支店（001） 普通 1234567 名義」
   */
  bank: InvoiceBank.default({ id: null, text: "", source: "none" }),
  /** 入金の記録の帯（E1、営業力 docs/08 §5。sales の会社の、出した請求書だけ）。sales が OFF の会社・下書きは省く */
  payments: InvoicePayments.nullable().optional(),
  /**
   * 取消（canUnissue）を止めているわけ（#173）。payment＝消していない入金が当たっている（DB の fn_unissue_invoice と同じ決まり）。
   * 設定で取消しを使える会社の出した請求書でだけ付ける＝画面は「⋯」の取消を薄く出し、下に「入金を削除すると取消できます」。ほかは null
   */
  unissueBlockedBy: z.enum(["payment"]).nullable().optional(),
  /**
   * 見積 → 請求書で税率が変わった（T2、§1-7 ②）：元の見積の taxes と請求書の taxes で、同じ区分の税率が違うとき。
   * date＝請求書の取引日（無ければ請求日）。変わっていない・元の見積が無いときは null
   */
  taxChangedFromQuote: TaxChangedFromQuote.nullable().optional(),
  /**
   * 納品書から起こした請求書（束ね請求。#266、docs/96）。notes＝載せた納品書（番号の順）。taxFromNotes＝{customer}の税を確定する書類が納品書＝
   * 消費税は納品書の額を足すだけ（明細は直せない＝納品書で直す）。納品書から起こしていなければ null
   */
  fromDeliveryNotes: z.object({
    notes: z.array(z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), issueDate: z.string() })),
    taxFromNotes: z.boolean(),
    /** 載せた納品書から作った発行済の{acceptance}の数（#266 1b。組合せ PDF「請求書＋{deliveryNote}＋{acceptance}」） */
    acceptances: z.number().int().default(0),
  }).nullable().optional(),
  /** 締め請求書（#267、docs/97）：期間・束ねた{deliveryNote}と{acceptance}の数・締め日・明細の形。締めでなければ null */
  closing: InvoiceClosing.nullable().optional(),
  /** 分割払い（#267 3b）：{customer}の既定とこの1枚の上書き（発行の確認の初めの値）。出したあとの予定は payments.schedule */
  paySchedule: InvoicePaySchedule.nullable().optional(),
  /** スイッチ「{acceptance}」（#266 1b の3段目。attach＝この1枚・null は{customer}に従う／partner＝{customer}／on＝いまの値） */
  acceptanceSwitch: z.object({ attach: z.boolean().nullable(), partner: z.boolean().nullable(), on: z.boolean() }).nullable().optional(),
  /** 定期請求（#268）：契約と請求月（帯「2026年11月分・営業力 利用契約 ›」）。定期でなければ null */
  recurring: InvoiceRecurring.nullable().optional(),
  /** 管理番号（#268 1.5b）：前のシステムの番号など社内の目印（紙に出さない・発行のあとでも直せる）。無ければ null */
  managementNo: z.string().nullable().optional(),
  /** 発行前の確認の番号（#268 1.5b）：手入力を許すか・自動のときの番号の見本。下書きのときだけ（発行済は null） */
  numbering: InvoiceNumbering.nullable().optional(),
  /** 取り込んだ請求書（#268 1.5c-1）：前のシステムで発行した未入金の請求書。紙は作らない（元の PDF を添付できる）・取消しできない。取込でなければ null */
  imported: InvoiceImported.nullable().optional(),
});
export type Invoice = z.infer<typeof Invoice>;

/** PUT /invoices/:id/acceptance・/delivery-notes/:id/acceptance-switch（#266 1b）：スイッチ「{acceptance}」（null＝{customer}に従う。発行のあとでも） */
export const AcceptanceSwitchUpdate = z.object({ attach: z.boolean().nullable() });
export type AcceptanceSwitchUpdate = z.infer<typeof AcceptanceSwitchUpdate>;

/** 起こす：受注した見積から */
export const InvoiceCreate = z.object({
  fromQuoteId: z.string().uuid().optional(),
  /** 分割の予定の回から起こす（042、docs/30） */
  fromInstallmentId: z.string().uuid().optional(),
  /** 発行済・未請求の納品書から起こす（束ね請求。#266、docs/96 決めごと14）。bundleMode＝全行／1枚1行 */
  fromDeliveryNoteIds: z.array(z.string().uuid()).min(1).max(50).optional(),
  bundleMode: z.enum(["lines", "perNote"]).optional(),
}).refine((x) => [!!x.fromQuoteId, !!x.fromInstallmentId, !!x.fromDeliveryNoteIds].filter(Boolean).length === 1, { message: "どの見積（分割の回・納品書）から作成するかを1つだけ指定します" });
export type InvoiceCreate = z.infer<typeof InvoiceCreate>;

/**
 * 下書きの保存。省いた欄は変えない（紙の上の欄と、行・内訳は別々に送れる。
 * 見積のエディタは行・内訳だけを送り、請求書の画面は紙の上の欄だけを送る）
 */
export const InvoiceSave = z.object({
  subject: z.string().trim().max(200, ESTIMATE_MSG.subjectTooLong).optional(),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  /** 帳票の取引日（T1。引渡しの日。行の税率を決める日の既定）。省けば変えない・null で外す（請求日を使う） */
  taxPointDate: TaxDate.nullable().optional(),
  siteAddress: z.string().trim().max(200).optional(),
  workPeriodText: z.string().max(100).optional(),
  notes: z.string().max(2000).optional(),
  /** 振込口座（043）。uuid＝この口座、null＝会社の既定に従う。省けば変えない */
  bankAccountId: z.string().uuid().nullable().optional(),
  sheets: z.array(SheetInput).max(50, ESTIMATE_MSG.sheetsTooMany).optional(),
  lines: z.array(LineInput).max(500, ESTIMATE_MSG.tooManyLines).optional(),
  /**
   * 版（データが消えうる不具合の直し、2026-10-13）：画面が読んだときの Invoice.updatedAt。違えば 409 INVOICE_STALE
   * （ほかのタブ・ほかの人の保存とぶつかった＝上書きせずに断る。画面は読み直して重ねる）。省けば見ない
   */
  version: z.string().max(40).optional(),
});
export type InvoiceSave = z.infer<typeof InvoiceSave>;

export const InvoiceSummary = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: InvoiceStatus,
  subject: z.string(),
  /** #302：{customer}の絞り（clientId）・一覧の名前は略称（空なら正式名称）・乗せたときは clientFullName */
  clientId: z.string().uuid().optional(),
  clientName: z.string(),
  clientFullName: z.string().optional(),
  honorific: z.string(),
  projectName: z.string(),
  quoteNo: z.string().nullable(),
  issueDate: z.string(),
  dueDate: z.string().nullable(),
  total: z.number().int(),
  /** 税抜（#302 A6） */
  subtotal: z.number().int().optional(),
  updatedAt: z.string(),
  /** 042：分割の回（一覧に「分割 1回目／3回 着手金」）。一括なら null */
  installment: z.object({ seq: z.number().int(), count: z.number().int(), label: z.string() }).nullable().optional(),
  /** 残と入金の状態（E1。sales の会社の、出した請求書だけ）。sales が OFF の会社は省く */
  balance: z.number().int().optional(),
  payState: InvoiceState.optional(),
  overdueDays: z.number().int().optional(),
  /** 締め請求書の締め日（#267）。締めでなければ省く */
  closingPeriodEnd: z.string().nullable().optional(),
  /** 定期請求の請求月（#268、YYYY-MM）。定期でなければ省く */
  recurringMonth: z.string().nullable().optional(),
  /** 管理番号（#268 1.5b）。無ければ省く */
  managementNo: z.string().nullable().optional(),
  /** 取り込んだ請求書（#268 1.5c-1）。取込でなければ省く */
  imported: z.boolean().optional(),
  /** 送付（#303 B3）：送付の記録から 未送付／送付済／取得済。下書きは省く */
  send: SendSummary.optional(),
});
export type InvoiceSummary = z.infer<typeof InvoiceSummary>;
export const InvoiceList = z.object({
  items: z.array(InvoiceSummary),
  /** 締めの画面の入口を出すか（#267）：締めの{customer}・{project}か締め請求書が1つでもある sales の会社 */
  closingAvailable: z.boolean().optional(),
  /** 定期請求の入口を出すか（#268）：定期の{project}か定期の請求書が1つでもある sales の会社 */
  recurringAvailable: z.boolean().optional(),
  /** 入金のタブの数（E1。sales の会社だけ。未入金は期限超過を含む） */
  payCounts: z.object({ unpaid: z.number().int(), overdue: z.number().int(), paid: z.number().int() }).optional(),
});
export type InvoiceList = z.infer<typeof InvoiceList>;

// ---------------------------------------------------------------------------
// 顧客の支払条件（payment_terms：締め日・支払月・支払日）。請求書の「お支払期限」の計算に使う。
// 都度／締め・どちらがインボイスか（007/008）は締め請求の段で画面に出す
// ---------------------------------------------------------------------------

/** 締め日・支払日。99＝末日 */
export const TermDay = z.union([z.literal(5), z.literal(10), z.literal(15), z.literal(20), z.literal(25), z.literal(99)]);
export type TermDay = z.infer<typeof TermDay>;
export const TERM_DAY_LABEL = (d: TermDay): string => (d === 99 ? "末日" : `${d}日`);
/** 支払月：0＝当月、1＝翌月、2＝翌々月 */
export const TermMonth = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export type TermMonth = z.infer<typeof TermMonth>;
export const TERM_MONTH_LABEL: Record<TermMonth, string> = { 0: "当月", 1: "翌月", 2: "翌々月" };

export const PaymentTerms = z.object({
  closingDay: TermDay,
  paymentMonthOffset: TermMonth,
  paymentDay: TermDay,
});
export type PaymentTerms = z.infer<typeof PaymentTerms>;

/** 既定（顧客に支払条件が無いとき）：月末締め・翌月末払い。DB の fn_payment_due_date と同じ */
export const DEFAULT_PAYMENT_TERMS: PaymentTerms = { closingDay: 99, paymentMonthOffset: 1, paymentDay: 99 };

/** 「末締め・翌月末払い」「20日締め・翌々月10日払い」（DB の fn_payment_terms_label と同じ文。#280 で「月末締め」から） */
export function paymentTermsLabel(t: PaymentTerms): string {
  const c = t.closingDay === 99 ? "末" : `${t.closingDay}日`;
  const d = t.paymentDay === 99 ? "末" : `${t.paymentDay}日`;
  return `${c}締め・${TERM_MONTH_LABEL[t.paymentMonthOffset]}${d}払い`;
}

/** POST /invoices/:id/issue の本文（#267 3b：分割払いの1枚の上書きを足した。省けば下書きの選択のまま） */
export const InvoiceIssueRequest = IssueRequest.extend({
  installments: InstallmentChoice.optional(),
  /** 手入力の番号（#268 1.5b。設定 → 帳票番号で「手入力を許す」の会社だけ）。省く・空なら自動 */
  documentNo: z.string().trim().max(50).optional(),
});
export type InvoiceIssueRequest = z.infer<typeof InvoiceIssueRequest>;
